import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { ImageVariantGrid, type ImageVariant } from '../components/widgets/ImageVariantGrid';
import { Skeleton } from '../components/ui/Skeleton';
import { Images, Content as ContentApi } from '../api/client';
import { Wand2, Shuffle, Hash, Plus } from 'lucide-react';
import { useStore } from '../store';
import { motion } from 'framer-motion';

export function ImageLab() {
  const [prompt, setPrompt] = useState('Hand-poured lavender soap, soft morning light, eucalyptus sprig, ceramic dish, warm minimal');
  const [aspect, setAspect] = useState('1:1');
  const [variants, setVariants] = useState<ImageVariant[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [variantCount, setVariantCount] = useState(4);
  const [trending, setTrending] = useState<string[]>([]);
  const pushToast = useStore((s) => s.pushToast);

  useEffect(() => {
    ContentApi.trending('handmade soap').then((arr) => setTrending(arr.map((t) => t.tag))).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await Images.generate({ prompt, n: variantCount, aspect_ratio: aspect });
      setVariants(result);
      setSelected(result[0]?.id ?? null);
      pushToast({ kind: 'success', message: `Generated ${result.length} image variants` });
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        kicker="// image lab · 04"
        title="Image generation"
        description="Tune the prompt, pick the aspect, ship a hero. Re-roll until it's right."
        actions={
          <Button variant="primary" leftIcon={<Wand2 size={13} />} onClick={handleGenerate} loading={loading}>
            {loading ? 'Generating…' : 'Generate'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <div className="space-y-4">
          <Card>
            <CardHeader kicker="// input" title="Prompt" />
            <Field kicker="// describe" label="Prompt" hint="Be vivid. Style, lighting, composition, mood.">
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} className="resize-none" />
            </Field>

            <div className="mt-4">
              <div className="kicker mb-2">aspect ratio</div>
              <div className="grid grid-cols-3 gap-1.5">
                {['1:1', '4:5', '3:4', '16:9', '9:16'].map((a) => (
                  <button
                    key={a}
                    onClick={() => setAspect(a)}
                    className={
                      'h-9 rounded-lg text-[12px] font-mono tracking-tight border transition ' +
                      (aspect === a
                        ? 'border-etsy bg-etsy-50 text-etsy-700 shadow-[0_0_0_3px_rgba(241,100,30,0.10)]'
                        : 'border-cream-300 bg-white text-ink-500 hover:text-ink-800 hover:border-cream-200')
                    }
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="kicker mb-2">variants</div>
              <div className="grid grid-cols-4 gap-1.5">
                {[1, 2, 3, 4, 6, 8].map((n) => (
                  <button
                    key={n}
                    onClick={() => setVariantCount(n)}
                    className={
                      'h-9 rounded-lg text-[12px] font-mono border transition ' +
                      (n === variantCount
                        ? 'border-etsy bg-etsy-50 text-etsy-700 shadow-[0_0_0_3px_rgba(241,100,30,0.10)]'
                        : 'border-cream-300 bg-white text-ink-500 hover:text-ink-800 hover:border-cream-200')
                    }
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card noise>
            <CardHeader kicker="// trends" title="What sells now" description="Top tags from Etsy search." />
            <div className="flex flex-wrap gap-1.5">
              {trending.length === 0
                ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-6 w-20" />)
                : trending.slice(0, 12).map((t) => (
                    <button
                      key={t}
                      onClick={() => setPrompt((p) => p + `, ${t}`)}
                      className="px-2.5 h-7 rounded-lg bg-cream-100 border border-cream-300 text-[11px] text-ink-600 hover:text-etsy hover:border-etsy-200 hover:bg-etsy-50 transition font-mono flex items-center gap-1"
                    >
                      <Hash size={10} className="opacity-50" />
                      {t}
                    </button>
                  ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader
              kicker="// output"
              title="Variants"
              description="Generated images. Click to select, or re-roll for a new batch."
              actions={
                <>
                  <Button variant="secondary" size="sm" leftIcon={<Shuffle size={12} />} onClick={handleGenerate}>
                    Re-roll all
                  </Button>
                  <Badge tone="brand">{variants.length || 0} ready</Badge>
                </>
              }
            />
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-2xl" />
                ))}
              </div>
            ) : variants.length === 0 ? (
              <EmptyLab onGenerate={handleGenerate} />
            ) : (
              <ImageVariantGrid variants={variants} selected={selected} onSelect={setSelected} onRegenerate={handleGenerate} />
            )}
          </Card>

          {selected && variants.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Card noise>
                <CardHeader kicker="// metadata" title="Selected variant" description="Image details for listing." />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-[12px]">
                  <Meta label="seed" value={variants.find((v) => v.id === selected)?.seed ?? '—'} />
                  <Meta label="prompt" value={prompt.slice(0, 40) + '…'} mono />
                  <Meta label="ratio" value={aspect} />
                  <Meta label="size" value="1024 × 1024" />
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="surface-soft p-3">
      <div className="kicker mb-1">{label}</div>
      <div className={'text-[12px] text-ink-800 truncate ' + (mono ? 'font-mono' : '')}>{value}</div>
    </div>
  );
}

function EmptyLab({ onGenerate }: { onGenerate: () => void }) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex h-12 w-12 rounded-2xl bg-cream-200 border border-cream-300 items-center justify-center mb-3">
        <Wand2 size={20} className="text-ink-500" strokeWidth={1.7} />
      </div>
      <div className="text-[13px] text-ink-800 mb-1 font-medium">Ready to render</div>
      <div className="text-[12px] text-ink-500 mb-4">Hit generate to create 4 variants from your prompt.</div>
      <Button variant="primary" leftIcon={<Wand2 size={13} />} onClick={onGenerate}>
        Generate
      </Button>
    </div>
  );
}
